import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback, useEffect } from 'react'

import { useLocalStorageState } from '~/hooks/useLocalStorageState'
import { useMediaQuery } from '~/theme/hooks'

import type { OverviewPage } from './types'
import { isValidPage } from './types'

export default function useOverviewPage(baseUrl: string) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const page = searchParams.get('view')
  const [storedPage, setStoredPage] = useLocalStorageState<OverviewPage>('view', { defaultValue: 'map' })

  const setPage = useCallback(
    (page: OverviewPage, replace?: boolean) => {
      const params = new URLSearchParams(searchParams.toString())
      params.set('view', page)
      const href = `${baseUrl}?${params}`
      if (replace) {
        router.replace(href)
      } else {
        router.push(href)
      }
    },
    [router, baseUrl, searchParams]
  )

  useEffect(() => {
    if (isValidPage(page)) {
      // The `view` search param is valid, update local storage
      setStoredPage(page)
    } else if (isValidPage(storedPage)) {
      // The `view` search param is invalid or not present, but we have a valid value in local storage
      setPage(storedPage, true)
    } else {
      // Go to the "map" view by default. This should only happen if the localStorage value is invalid somehow
      setPage('map', true)
    }
  }, [page, router, setPage, setStoredPage, storedPage])

  return {
    page: isValidPage(page) ? page : null,
    setPage
  }
}

/**
 * Returns true if the screen size is large enough to show the map. We show the map for medium and
 * above screens, which comes out to a min-width of 900px.
 */
export function useShowMap() {
  return !useMediaQuery((theme) => theme.breakpoints.down('md'))
}

/**
 * Normalizes a string by removing diacritics and converting to lowercase. Diacritics are removed by
 * converting the string to its Unicode "Normalization Form Canonical Decomposition" (NFD)
 * representation and then removing all "Combining Diacritical Marks" (the range U+0300 to U+036F).
 *
 * See also:
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize
 * https://en.wikipedia.org/wiki/Combining_character#Unicode_ranges
 * https://en.wikipedia.org/wiki/Unicode_equivalence#Example
 *
 * @param str The string to normalize
 */
export function normalizeStr(str: string): string
export function normalizeStr(str?: string): string | undefined
export function normalizeStr(str?: string) {
  return str
    ?.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}
